/**
 * @file ContinuingReviewFormNeedsAction.tsx
 * @description
 * Respond-to-feedback editor for a CONTINUING_REVIEW submission in NEEDS_ACTION.
 * Modeled closely on SubmissionFormNeedsAction.tsx (feedback validation + the
 * "Send Revised Submission For IRB Review" flow) but driven by the
 * continuing-review schema.
 *
 * Mounted at /continuing-reviews/:id/respond.
 *
 * Gating: hidden unless isContinuingReviewsUiEnabled().
 *
 * @notes
 * - useFormLock / logFormOperation / extractFieldDefinitions are intentionally
 *   duplicated from the initial-submission page to avoid touching the existing
 *   (no-break) pages. Autosave `createdAt` literal preserved verbatim.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useForm } from 'react-hook-form';
import UserNavBar from '../components/UserNavBar';
import { useAutosave } from '../hooks/useAutosave';
import { useFormProgress } from '../hooks/useFormProgress';
import { FormSubmissionFields, FormData } from '../components/FormSubmissionFields';
import { submitDocumentPackage } from '../services/submissionDocumentPackage';
import { SubmissionProgressModal } from '../components/SubmissionProgressModal';
import { useSupabase } from '../contexts/SupabaseContext';
import FeedbackAndResponses from '../components/FeedbackAndResponses';
import { ReviewCycle } from '../types/submission';
import withIdleTimer from '../hoc/withIdleTimer';
import { isContinuingReviewsUiEnabled } from '../services/featureFlagService';
import { getFormSchemaConfig } from '../forms/formSchemaConfig';
import { submissionDetailsRoute } from '../utils/submissionRoutes';

function logFormOperation(operation: string, details: Record<string, any>, error?: any) {
  const timestamp = new Date().toISOString();
  const logObj = {
    operation,
    timestamp,
    details,
    ...(error && {
      error:
        typeof error === 'object'
          ? { message: error.message, name: error.name, stack: error.stack }
          : error,
    }),
  };

  if (error) {
    console.error(`CR_FORM_LOG [${operation}]:`, logObj);
  } else {
    console.log(`CR_FORM_LOG [${operation}]:`, logObj);
  }

  return logObj;
}

const CR_CONFIG = getFormSchemaConfig('CONTINUING_REVIEW');
const crSections = CR_CONFIG.sections;


// Local copy of the cross-tab form lock (duplicated to avoid touching the
// existing no-break pages).
function useFormLock(submissionId: string | null, lockTimeoutMinutes = 30) {
  const [isLocked, setIsLocked] = useState(false);
  const [lockMessage, setLockMessage] = useState('');

  useEffect(() => {
    if (!submissionId) return;

    if (!window.name || window.name.length === 0) {
      window.name = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    const tabId = window.name;
    const lockKey = `form_lock_${submissionId}`;
    const lockTimeoutMs = lockTimeoutMinutes * 60 * 1000;

    const safeGetItem = (key: string) => {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        console.error('Error accessing localStorage:', e);
        return null;
      }
    };

    const safeSetItem = (key: string, value: string): boolean => {
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (e) {
        console.error('Error writing to localStorage:', e);
        return false;
      }
    };

    const safeRemoveItem = (key: string): boolean => {
      try {
        localStorage.removeItem(key);
        return true;
      } catch (e) {
        console.error('Error removing from localStorage:', e);
        return false;
      }
    };

    const checkAndSetLock = () => {
      const existingLockData = safeGetItem(lockKey);

      if (existingLockData) {
        try {
          const lock = JSON.parse(existingLockData);
          const now = Date.now();
          const lockAge = now - lock.timestamp;

          if (lockAge > lockTimeoutMs) {
            setNewLock();
            return;
          }

          if (lock.tabId !== tabId) {
            const minutesAgo = Math.floor(lockAge / 60000);
            setLockMessage(
              `This form was opened ${minutesAgo} minute${minutesAgo !== 1 ? 's' : ''} ago in another tab or window.`
            );
            setIsLocked(true);
            return;
          }

          lock.timestamp = now;
          safeSetItem(lockKey, JSON.stringify(lock));
        } catch (e) {
          console.error('Invalid lock data, creating new lock');
          setNewLock();
        }
      } else {
        setNewLock();
      }
    };

    const setNewLock = () => {
      const lockData = {
        tabId: tabId,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        pageUrl: window.location.href,
      };

      safeSetItem(lockKey, JSON.stringify(lockData));
      setIsLocked(false);
    };

    checkAndSetLock();

    const interval = setInterval(() => {
      if (isLocked) {
        const existingLockData = safeGetItem(lockKey);
        if (!existingLockData) {
          setNewLock();
          setIsLocked(false);
        }
      } else {
        checkAndSetLock();
      }
    }, 10000);

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === lockKey) {
        if (!e.newValue && !isLocked) {
          setTimeout(checkAndSetLock, Math.random() * 1000);
        } else if (e.newValue && !isLocked) {
          checkAndSetLock();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);

      const existingLockData = safeGetItem(lockKey);
      if (existingLockData) {
        try {
          const lock = JSON.parse(existingLockData);
          if (lock.tabId === tabId) {
            safeRemoveItem(lockKey);
          }
        } catch (e) {
          console.error('Error parsing lock data during cleanup:', e);
        }
      }
    };
  }, [submissionId, lockTimeoutMinutes, isLocked]);

  return { isLocked, lockMessage };
}

const FormComponent: React.FC = () => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({ mode: 'onChange' });

  const navigate = useNavigate();
  const { id: respondSubmissionId } = useParams<{ id?: string }>();
  const supabase = useSupabase();
  const { user, signOut, loading: authLoading, sessionExpired } = useAuth();

  const crUiEnabled = isContinuingReviewsUiEnabled();

  const [documentsPending, setDocumentsPending] = useState(true);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submissionStatus, setSubmissionStatus] = useState<string>('NEEDS_ACTION');


  const isFetchingRef = useRef(false);
  const userIdRef = useRef<string | null>(null);

  const watchAll = watch();
  const userId = user?.id || 'anonymous-user';

  const { sectionProgress } = useFormProgress(crSections as any, watchAll);

  const sectionRefs = useMemo(
    () =>
      Array(crSections.length)
        .fill(null)
        .map(() => React.createRef<HTMLDivElement>()),
    []
  );

  const scrollToSection = (index: number) => {
    if (sectionRefs[index] && sectionRefs[index].current) {
      sectionRefs[index].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const redirectToDetails = useCallback(
    (existingId: string) => {
      navigate(submissionDetailsRoute(existingId), {
        replace: true,
        state: {
          accessMessage: 'You can view this submission, but only the submission owner can edit it.',
        },
      });
    },
    [navigate]
  );

  const autosaveConfig =
    !loading && submissionId
      ? {
          formData: watchAll,
          submissionId,
          userId,
          createdAt: '2025-02-20T14:30:00Z', // example
        }
      : null;

  const { savingStatus, handleImmediateAutosave } = useAutosave(autosaveConfig);

  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [hasFinalError, setHasFinalError] = useState(false);
  const [finalErrorMessage, setFinalErrorMessage] = useState('');
  const [hasFinalSuccess, setHasFinalSuccess] = useState(false);
  const [finalSuccessMessage, setFinalSuccessMessage] = useState('');

  const stepMessages = [
    'Checking required form fields...',
    'Storing locked form as PDF...',
    'Storing locked form as Markdown...',
    'Saving the verified document selection...',
    'Redirecting to dashboard...',
  ];

  const feedbackValidationRef = useRef<(() => string[] | null) | null>(null);

  useEffect(() => {
    if (sessionExpired) {
      navigate('/signin');
    }
  }, [sessionExpired, navigate]);

  const { isLocked, lockMessage } = useFormLock(submissionId);

  const loadExistingCR = useCallback(
    async (existingId: string) => {
      if (isFetchingRef.current || !user?.id) {
        return;
      }

      isFetchingRef.current = true;
      setLoading(true);

      try {
        const { data, error } = await supabase
          .from('submissions')
          .select('*')
          .eq('id', existingId)
          .eq('user_id', user?.id || '')
          .single();

        if (error || !data || !user?.id) {
          console.warn('No existing continuing review found!', error);
          redirectToDetails(existingId);
          return;
        }

        if (data.submission_type !== 'CONTINUING_REVIEW') {
          console.warn('Submission is not a continuing review, redirecting to details.');
          redirectToDetails(existingId);
          return;
        }

        setSubmissionId(data.id);
        setSubmissionStatus(data.status || 'NEEDS_ACTION');

        if (data.data && typeof data.data === 'object') {
          Object.entries(data.data).forEach(([key, value]) => {
            setValue(key as any, value, { shouldValidate: false });
          });
        }
      // The document workspace loads current rows independently of saved form data.

      } catch (err) {
        console.error('Error loading continuing review:', err);
        redirectToDetails(existingId);
      } finally {
        isFetchingRef.current = false;
        setLoading(false);
      }
    },
    [redirectToDetails, supabase, user, setValue]
  );

  useEffect(() => {
    if (user?.id !== userIdRef.current) {
      userIdRef.current = user?.id || null;
    }

    if (authLoading) {
      return;
    }

    if (!crUiEnabled) {
      setLoading(false);
      return;
    }

    if (!user?.id) {
      setLoading(false);
      return;
    }

    if (respondSubmissionId) {
      logFormOperation('loadExistingCR', { respondSubmissionId });
      loadExistingCR(respondSubmissionId);
    } else {
      setLoading(false);
      navigate('/dashboard', { replace: true });
    }
  }, [user?.id, authLoading, crUiEnabled, respondSubmissionId, loadExistingCR, navigate]);

  const finalizeSubmission = async (data: FormData) => {
    if (!submissionId) {
      alert('No submission ID found. Cannot finalize submission.');
      return;
    }

    if (feedbackValidationRef.current) {
      const feedbackErrors = feedbackValidationRef.current();
      if (feedbackErrors) {
        const errorMessage =
          'Please fix the following issues before submitting:\n\n• ' + feedbackErrors.join('\n• ');
        alert(errorMessage);
        return;
      }
    }

    if (documentsPending) { alert('Finish or discard pending uploads and resolve document errors before submitting.'); return; }

    setHasFinalError(false);
    setFinalErrorMessage('');
    setHasFinalSuccess(false);
    setFinalSuccessMessage('');

    setProgressModalOpen(true);
    setProgressStep(0);

    try {
      await submitDocumentPackage(submissionId, data, crSections, setProgressStep);
      setSubmissionStatus('IRB_REVIEW');

      setProgressStep(4);

      setTimeout(() => {
        setHasFinalSuccess(true);
        setFinalSuccessMessage('Submission finalized successfully!');
        setTimeout(() => {
          setProgressModalOpen(false);
          navigate('/dashboard');
        }, 1200);
      }, 600);
    } catch (error) {
      console.error('Error in finalizeSubmission:', error);
      setHasFinalError(true);
      setFinalErrorMessage(error instanceof Error ? error.message : String(error));
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const onSubmit = (data: FormData) => {
    finalizeSubmission(data);
  };

  const handleFeedbackResponseSaved = (responses: ReviewCycle['response']) => {
    console.log('Feedback responses saved successfully', responses);
  };

  if (!crUiEnabled) {
    return (
      <div className="flex flex-col h-screen">
        <UserNavBar user={user} onSignOut={handleLogout} />
        <div className="h-16"></div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-6 max-w-md bg-gray-50 border border-gray-200 rounded-lg">
            <h2 className="text-xl font-bold text-gray-800 mb-3">Continuing reviews are not enabled</h2>
            <p className="mb-4 text-gray-600">
              Continuing reviews are not currently available. Please check back later or contact support
              if you believe this is an error.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-[#F55D3E] text-white rounded-md"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="flex flex-col h-screen">
        <UserNavBar user={user} onSignOut={handleLogout} />
        <div className="h-16"></div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F55D3E] mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying your account...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLocked) {
    return (
      <div className="flex flex-col h-screen">
        <UserNavBar user={user} onSignOut={handleLogout} />
        <div className="h-16"></div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-6 max-w-md bg-amber-50 border border-amber-200 rounded-lg">
            <div className="text-amber-600 text-3xl mb-2">⚠️</div>
            <h2 className="text-xl font-bold text-gray-800 mb-3">This Form Is Already Open</h2>
            <p className="mb-4 text-gray-600">
              {lockMessage || 'This submission is currently being edited in another browser tab.'}
            </p>
            <p className="mb-4 text-gray-600">Please continue editing in that tab to avoid conflicts.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-[#F55D3E] text-white rounded-md"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      <UserNavBar user={user} onSignOut={handleLogout} />

      <div className="h-16"></div>

      <div className="fixed top-28 right-6 z-50">
        {savingStatus === 'saving' && (
          <div className="p-2 rounded-md bg-[#F9A594] text-[#343a40] shadow-md">Saving...</div>
        )}
        {savingStatus === 'saved' && (
          <div className="p-2 rounded-md bg-[#D4F5E9] text-[#343a40] shadow-md">Saved</div>
        )}
        {savingStatus === 'error' && (
          <div className="p-2 rounded-md bg-[#B83EF5] text-white shadow-md">Autosave Error</div>
        )}
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="hidden lg:block w-64 shrink-0 bg-white shadow-md border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            <h2 className="text-lg font-bold text-[#343a40] mb-3">Continuing Review Sections</h2>
            <ul className="space-y-2">
              {sectionProgress.map((section, idx) => (
                <li key={`section-${idx}`} className="mb-3">
                  <button
                    onClick={() => scrollToSection(idx)}
                    className="w-full text-left py-2 px-3 rounded text-sm font-medium transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#F55D3E] focus:ring-opacity-50"
                  >
                    {section.heading}
                  </button>
                  <div className="mt-1 px-3 flex items-center justify-between">
                    <span
                      className={`text-xs font-medium ${
                        section.completion === 100
                          ? 'text-green-600'
                          : section.completion > 50
                          ? 'text-yellow-600'
                          : 'text-red-600'
                      }`}
                    >
                      {section.completion}% complete
                    </span>
                    <div className="w-16 h-1.5 bg-gray-200 rounded-full">
                      <div
                        className={`h-1.5 rounded-full ${
                          section.completion === 100
                            ? 'bg-green-500'
                            : section.completion > 50
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${section.completion}%` }}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500">
              <p>
                Questions? Check out our{' '}
                <a
                  href="https://timelyirb.com/faqs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#F55D3E] hover:underline"
                >
                  FAQs
                </a>
                , find more info on{' '}
                <a
                  href="https://timelyirb.com/review-process"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#F55D3E] hover:underline"
                >
                  our review process
                </a>
                , or email{' '}
                <a href="mailto:support@timelyirb.com" className="text-[#F55D3E] hover:underline">
                  support@timelyirb.com
                </a>
              </p>
            </div>
          </div>
        </div>

        <div className="min-w-0 flex-1 overflow-auto relative">
          {loading ? (
            <div className="p-6 text-center">Loading...</div>
          ) : (
            <FormSubmissionFields
              onSubmit={onSubmit}
              register={register}
              errors={errors}
              watchAll={watchAll}
              setValue={setValue}
              handleImmediateAutosave={handleImmediateAutosave}
              handleSubmit={handleSubmit}
              savingStatus={savingStatus}
              loading={false}
              sectionRefs={sectionRefs}

              submissionId={submissionId}
              userId={user?.id}
              onDocumentsPendingChange={setDocumentsPending}
              sections={crSections}
              title={CR_CONFIG.title}
              hideSubmitButton={submissionStatus === 'NEEDS_ACTION'}
            />
          )}
        </div>
      </div>

      <SubmissionProgressModal
        isOpen={progressModalOpen}
        stepMessages={stepMessages}
        currentStep={progressStep}
        hasError={hasFinalError}
        errorMessage={finalErrorMessage}
        hasSuccess={hasFinalSuccess}
        successMessage={finalSuccessMessage}
        onClose={() => {
          setProgressModalOpen(false);
        }}
        onRetry={() => {
          setProgressModalOpen(false);
          setProgressStep(0);
          setHasFinalError(false);
          setFinalErrorMessage('');
        }}
        showCloseButton={false}
      />

      {submissionStatus === 'NEEDS_ACTION' && submissionId && (
        <div className="mt-8 border-t border-gray-300 pt-6">
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">IRB Feedback & Responses</h2>
            <button
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={documentsPending}
              className="px-4 py-2 bg-[#F55D3E] text-white rounded-md font-semibold hover:bg-[#d94a30] focus:outline-none focus:ring-2 focus:ring-[#F55D3E]"
            >
              Send Revised Submission For IRB Review
            </button>
          </div>

          <div className="bg-white rounded-lg shadow p-4 max-h-[33vh] overflow-y-auto">
            <FeedbackAndResponses
              submissionId={submissionId}
              mode="edit"
              onSave={handleFeedbackResponseSaved}
              validateAndGetErrors={(validateFn) => {
                feedbackValidationRef.current = validateFn;
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const ContinuingReviewFormNeedsAction = withIdleTimer(FormComponent);
export default ContinuingReviewFormNeedsAction;
